import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import PhotoIcon from '@mui/icons-material/Photo';

export type ExportPlanPanelProps = {
  open: boolean;
  onClose: () => void;
  onExport: (format: 'pdf' | 'png' | 'jpeg') => void;
};

const LOCAL_STORAGE_KEY = 'selectedExportFormat';

export const ExportPlanPanel: React.FC<ExportPlanPanelProps> = ({
  open,
  onClose,
  onExport,
}) => {
  const [selected, setSelected] = useState<'pdf' | 'png' | 'jpeg' | null>(null);

  // Read selection from localStorage on panel open
  useEffect(() => {
    if (open) {
      const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (stored === 'pdf' || stored === 'png' || stored === 'jpeg') {
        setSelected(stored);
      } else {
        setSelected(null);
      }
    }
  }, [open]);

  const handleSelect = (format: 'pdf' | 'png' | 'jpeg') => {
    setSelected(format);
    localStorage.setItem(LOCAL_STORAGE_KEY, format);
    onExport(format);
  };

  if (!open) return null;

  // Custom colors for highlight
  const selectedBackgrounds: Record<'pdf' | 'png' | 'jpeg', string> = {
    pdf: '#f6f7f8',
    png: '#faebea',
    jpeg: '#f5eee7',
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 20,
        left: 100,
        zIndex: 1300,
      }}
    >
      <Paper
        elevation={6}
        sx={{
          p: 2.5,
          borderRadius: '16px',
          background: '#fff',
          width: 400,
          height: 'fit-content',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '1.12rem',
            mb: 2,
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
          }}
        >
          <img
            src="../images/export-svgrepo-com.svg"
            alt="Export"
            style={{ width: 22, height: 22, opacity: 0.88 }}
          />
          Export Plan
        </Typography>

        <List sx={{ p: 0 }}>
          <ListItemButton
            selected={selected === 'pdf'}
            sx={{
              borderRadius: 2,
              mb: 1,
              backgroundColor: selected === 'pdf' ? selectedBackgrounds.pdf : 'transparent',
            }}
            onClick={() => handleSelect('pdf')}
          >
            <ListItemIcon>
              <PictureAsPdfIcon color="error" />
            </ListItemIcon>
            <ListItemText
              primary={<Typography sx={{ fontWeight: 600 }}>PDF</Typography>}
              secondary="Professional document with sign details"
            />
          </ListItemButton>

          <ListItemButton
            selected={selected === 'png'}
            sx={{
              borderRadius: 2,
              mb: 1,
              backgroundColor: selected === 'png' ? selectedBackgrounds.png : 'transparent',
            }}
            onClick={() => handleSelect('png')}
          >
            <ListItemIcon>
              <ImageIcon color="primary" />
            </ListItemIcon>
            <ListItemText
              primary={<Typography sx={{ fontWeight: 600 }}>PNG</Typography>}
              secondary="High quality image with transparency"
            />
          </ListItemButton>

          <ListItemButton
            selected={selected === 'jpeg'}
            sx={{
              borderRadius: 2,
              backgroundColor: selected === 'jpeg' ? selectedBackgrounds.jpeg : 'transparent',
            }}
            onClick={() => handleSelect('jpeg')}
          >
            <ListItemIcon>
              <PhotoIcon sx={{ color: '#a07a43' }} />
            </ListItemIcon>
            <ListItemText
              primary={<Typography sx={{ fontWeight: 600 }}>JPEG</Typography>}
              secondary="Compressed image format"
            />
          </ListItemButton>
        </List>
      </Paper>
    </Box>
  );
};
